import React, { useCallback, useEffect, useState } from 'react';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  Stack,
  TextField,
  Typography,
  Button as MuiButton,
} from '@mui/material';
import { useNotify } from 'react-admin';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import HistoryIcon from '@mui/icons-material/History';
import LabelIcon from '@mui/icons-material/Label';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import {
  LabelRun,
  PickupRunPreview,
  RunOutcome,
  RunScope,
  downloadRunPdf,
  fetchPickupRuns,
  previewPickupRun,
  resendPickupRun,
} from './pickupRunApi';

/** What each outcome means to someone reading the log, not to the code. */
const OUTCOME_LABEL: Record<RunOutcome, { text: string; color: 'default' | 'success' | 'warning' | 'error' }> = {
  built: { text: 'Built, not sent', color: 'default' },
  sent: { text: 'Sent', color: 'success' },
  send_skipped: { text: 'Not sent', color: 'warning' },
  no_parcels: { text: 'Nothing to collect', color: 'default' },
  failed: { text: 'Failed', color: 'error' },
};

const formatDay = (day: string) => {
  const date = new Date(`${day}T12:00:00Z`);
  if (Number.isNaN(date.getTime())) return day;
  return new Intl.DateTimeFormat('en-CA', {
    timeZone: 'UTC',
    weekday: 'short',
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  }).format(date);
};

const formatSentAt = (iso: string) => {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return iso;
  return new Intl.DateTimeFormat('en-CA', {
    timeZone: 'America/Toronto',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  }).format(date);
};

/** YYYY-MM-DD for tomorrow, the day the next scheduled run targets. */
const tomorrow = () => {
  const date = new Date(Date.now() + 24 * 60 * 60 * 1000);
  return new Intl.DateTimeFormat('en-CA', {
    timeZone: 'America/Toronto',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(date);
};

const ParcelList = ({ parcels }: { parcels: LabelRun['parcels'] }) => (
  <Stack spacing={0.5}>
    {parcels.map((parcel, index) => (
      <Typography key={parcel.funeralId} variant="body2">
        {`${index + 1}. `}
        <strong>{parcel.deceasedName}</strong>
        {'  →  '}
        {parcel.recipientName}
        {parcel.city ? `, ${[parcel.city, parcel.province].filter(Boolean).join(' ')}` : ''}
        {'  '}
        {parcel.hasLabel ? (
          <Typography component="span" variant="caption" color="text.secondary">
            {parcel.trackingNumber ?? 'label'}
          </Typography>
        ) : (
          <Chip size="small" color="warning" icon={<WarningAmberIcon />} label="no label" />
        )}
      </Typography>
    ))}
  </Stack>
);

const RunRow = ({ run }: { run: LabelRun }) => {
  const notify = useNotify();
  const outcome = OUTCOME_LABEL[run.outcome] ?? { text: run.outcome, color: 'default' as const };

  return (
    <Accordion disableGutters>
      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          spacing={1}
          alignItems={{ xs: 'flex-start', sm: 'center' }}
          sx={{ width: '100%', pr: 2 }}
        >
          <Typography variant="body2" sx={{ minWidth: 170 }}>
            <strong>{formatDay(run.pickupDate)}</strong>
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ minWidth: 190 }}>
            {`${run.parcelCount} parcel${run.parcelCount === 1 ? '' : 's'} · ${
              run.trigger === 'manual' ? `resent by ${run.triggeredBy ?? 'staff'}` : 'scheduled'
            } · ${formatSentAt(run.createdAt)}`}
          </Typography>
          <Chip size="small" color={outcome.color} label={outcome.text} />
          {run.pdfError && <Chip size="small" color="warning" label="No PDF backup" />}
          {run.missingLabelCount > 0 && (
            <Chip
              size="small"
              color="warning"
              icon={<WarningAmberIcon />}
              label={`${run.missingLabelCount} without a label`}
            />
          )}
        </Stack>
      </AccordionSummary>
      <AccordionDetails>
        {run.error && (
          <Alert severity="warning" sx={{ mb: 1 }}>
            {run.error}
          </Alert>
        )}
        {run.pdfError && (
          <Alert severity="warning" sx={{ mb: 1 }}>
            <strong>{`No PDF backup: the .zpl ${
              run.outcome === 'sent' ? 'was sent' : 'was stored'
            } without it.`}</strong>{' '}
            {run.pdfError}
          </Alert>
        )}
        {run.parcels.length ? (
          <ParcelList parcels={run.parcels} />
        ) : (
          <Typography variant="body2" color="text.secondary">
            Nothing was going out that day.
          </Typography>
        )}
        {/* Gated on having parcels rather than on the stored .zpl: the PDF is
            re-rendered from the run's own manifest, so what it needs is parcels
            to draw, not a stored bundle. */}
        {run.parcelCount > 0 && (
          <Box sx={{ mt: 1.5 }}>
            <MuiButton
              size="small"
              startIcon={<LabelIcon />}
              onClick={() => {
                void downloadRunPdf(run).catch((error: unknown) =>
                  notify(
                    `Could not download the labels: ${
                      error instanceof Error ? error.message : 'unknown error'
                    }`,
                    { type: 'error' },
                  ),
                );
              }}
            >
              Download the labels (PDF)
            </MuiButton>
          </Box>
        )}
      </AccordionDetails>
    </Accordion>
  );
};

/**
 * Confirmation before a resend, showing exactly what the second stack of paper
 * will contain — including any parcel with no label, which resend reports rather
 * than fixes.
 */
const ResendDialog = ({
  date,
  onClose,
  onDone,
}: {
  date: string;
  onClose: () => void;
  onDone: () => void;
}) => {
  const notify = useNotify();
  const [preview, setPreview] = useState<PickupRunPreview | null>(null);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);

  useEffect(() => {
    setLoading(true);
    void previewPickupRun(date)
      .then(setPreview)
      .catch((error: unknown) =>
        notify(
          `Could not read that date: ${error instanceof Error ? error.message : 'unknown error'}`,
          { type: 'error' },
        ),
      )
      .finally(() => setLoading(false));
  }, [date, notify]);

  const handleResend = async () => {
    setSending(true);
    try {
      const run = await resendPickupRun(date);
      notify(
        `Rebuilt ${run.parcelCount} parcel${run.parcelCount === 1 ? '' : 's'} for ${formatDay(date)}`,
        { type: 'success' },
      );
      onDone();
      onClose();
    } catch (error) {
      notify(
        `Resend failed: ${error instanceof Error ? error.message : 'unknown error'}`,
        { type: 'error' },
      );
    } finally {
      setSending(false);
    }
  };

  return (
    <Dialog open onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{`Resend the labels for ${formatDay(date)}?`}</DialogTitle>
      <DialogContent>
        <DialogContentText sx={{ mb: 2 }}>
          This rebuilds the stack from labels that were already purchased. It never buys a new
          shipment, and it never replaces the earlier send — both will show in the log.
        </DialogContentText>
        {loading && <CircularProgress size={20} />}
        {preview && !preview.parcels.length && (
          <Alert severity="info">No parcels are scheduled for collection that day.</Alert>
        )}
        {preview && preview.parcels.length > 0 && (
          <Stack spacing={1}>
            <ParcelList parcels={preview.parcels} />
            {preview.missingLabelCount > 0 && (
              <Alert severity="warning">
                {`${preview.missingLabelCount} of ${preview.parcels.length} parcels have no label. ` +
                  'Resending will not create one — buy the label first if you want it in this stack.'}
              </Alert>
            )}
          </Stack>
        )}
      </DialogContent>
      <DialogActions>
        <MuiButton onClick={onClose} disabled={sending}>
          Cancel
        </MuiButton>
        <MuiButton
          onClick={() => void handleResend()}
          disabled={sending || loading || !preview?.parcels.length}
          startIcon={sending ? <CircularProgress size={16} color="inherit" /> : undefined}
        >
          {sending ? 'Rebuilding…' : 'Resend'}
        </MuiButton>
      </DialogActions>
    </Dialog>
  );
};

/**
 * The pickup-run panel, beside the scheduled Canada Post pickups.
 *
 * A schedule first: it shows the pickups still to come — today included, since
 * the runner is out with that stack now — and it empties as the days pass. It
 * used to be a rolling log with no date filter at all, so yesterday's run was
 * still the headline this morning and a resend sat somewhere else in the list
 * from the run it repeated. Everything it no longer shows is one button away
 * under "Previous runs"; nothing is deleted, merged, or hidden.
 *
 * Answers the two questions staff actually have: what is going to the runner,
 * and can I send it again. Resend rebuilds from purchased labels only — every
 * shipment costs money and carries its own tracking number — and it appends,
 * so both sends stay in the log.
 *
 * Days the cron found nothing to collect are recorded but not listed: six days
 * in seven are such a day, and listing them left a page of "0 parcels · Nothing
 * to collect" with the real runs buried in it. The line under the list is what
 * keeps that honest — an empty panel and a dead cron must never read the same.
 */
export const PickupRunsPanel = () => {
  const [scope, setScope] = useState<RunScope>('upcoming');
  const [runs, setRuns] = useState<LabelRun[]>([]);
  const [lastRunAt, setLastRunAt] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [resendDate, setResendDate] = useState<string | null>(null);
  const [date, setDate] = useState(tomorrow);

  const reload = useCallback(() => {
    setLoading(true);
    void fetchPickupRuns(scope)
      .then((data) => {
        setRuns(data.runs);
        setLastRunAt(data.lastRunAt);
        setError(null);
      })
      .catch((err: unknown) =>
        setError(err instanceof Error ? err.message : 'Could not load the pickup-run log'),
      )
      .finally(() => setLoading(false));
  }, [scope]);

  useEffect(() => reload(), [reload]);

  const upcoming = scope === 'upcoming';

  return (
    <Box sx={{ mx: 2, mt: 2 }}>
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        spacing={1}
        alignItems={{ xs: 'stretch', sm: 'center' }}
        justifyContent="space-between"
        sx={{ mb: 1 }}
      >
        <Typography variant="subtitle2">
          {upcoming ? 'Upcoming Rideau pickups' : 'Previous Rideau pickups'}
        </Typography>
        <Stack direction="row" spacing={1} alignItems="center">
          <MuiButton
            size="small"
            startIcon={<HistoryIcon />}
            onClick={() => setScope(upcoming ? 'previous' : 'upcoming')}
          >
            {upcoming ? 'Previous runs' : 'Back to upcoming'}
          </MuiButton>
          <TextField
            size="small"
            type="date"
            label="Pickup date"
            value={date}
            onChange={(event) => setDate(event.target.value)}
            InputLabelProps={{ shrink: true }}
          />
          <MuiButton size="small" disabled={!date} onClick={() => setResendDate(date)}>
            Resend labels
          </MuiButton>
        </Stack>
      </Stack>

      {error && <Alert severity="warning">{`Could not load the pickup-run log: ${error}`}</Alert>}
      {!error && loading && <CircularProgress size={20} />}
      {!error && !loading && runs.length === 0 && (
        <Typography variant="body2" color="text.secondary">
          {upcoming
            ? 'Nothing is scheduled for collection. The labels for a day are built at noon the day before.'
            : 'No previous pickups are recorded.'}
        </Typography>
      )}
      {runs.length > 0 && (
        <Box sx={{ border: 1, borderColor: 'divider', borderRadius: 1 }}>
          {runs.map((run, index) => (
            <React.Fragment key={run.id}>
              {index > 0 && <Divider />}
              <RunRow run={run} />
            </React.Fragment>
          ))}
        </Box>
      )}

      {/* The heartbeat, and the reason the list can be empty. Without it a dead
          cron and a week with nothing to collect read exactly the same, which
          is why the empty runs are recorded at all.

          No count of quiet days: the old line said "6 days with nothing to
          collect are not listed", which was the ALL-TIME total wearing the
          grammar of a recent window and would have read "310 days" within a
          year. The fact that they are left out is what the reader needs; the
          tally never meant anything. */}
      {!error && !loading && (
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ display: 'block', mt: 1 }}
        >
          {lastRunAt
            ? `Days with nothing to collect are not listed · the job last ran ${formatSentAt(
                lastRunAt,
              )}`
            : 'The build job has not run yet.'}
        </Typography>
      )}

      {resendDate && (
        <ResendDialog
          date={resendDate}
          onClose={() => setResendDate(null)}
          onDone={reload}
        />
      )}
    </Box>
  );
};
